import React, { useState } from "react";
import styles from "./OptionsEditor.module.scss";
import UpdateTemplateForm from "../UpdateTemplateForm/UpdateTemplateForm";
import { Button, Colors } from "@factorearth/component-library";
import SingleChevron from "assets/icons/SingleChevron";
import { Option, User } from "db/definitions";
import useCustomForm from "hooks/useCustomForm";

interface OptionsEditorProps {
	index: number; // The index of the option in the list
	template: Option;
	handleSubmitOption: (item: any) => Promise<void>; // Callback for submitting an option
	handleRemoveOption: (data: { optionToRemove: any; index: number }) => void; // Callback for removing an option
	handleShowDeleteModal: (cb: () => void, docType: string) => void; // Callback for showing the delete modal
	handleRearrangeOptions: (orderType: string, currentIndex: number, newIndex: number) => void; // Callback for rearranging options
	online: boolean; // Whether the app is online
	userDoc: User; // User document
	isLast: boolean; // Whether this is the last option in the list
	color: Colors;
}

function OptionsEditor(props: OptionsEditorProps) {
	const { index, template, handleSubmitOption, handleRemoveOption, handleShowDeleteModal, handleRearrangeOptions, online, userDoc, isLast, color } = props;
	const [editOpen, setEditOpen] = useState(false);

	const { values, handleChange, handleSubmit, handleUpdateMultiplePropsAtOnce } = useCustomForm(template, handleSubmitOption);

	function toggleEditOpen() {
		setEditOpen(!editOpen);
	}

	/**
	 * Up will move the option "up" in the list, so actually moving the element's index down.
	 * @param {string} direction to move the option
	 */
	function handleRearrange(direction?: string | undefined) {
		const modifier = direction === "UP" ? -1 : 1;
		const newIndex = index + modifier;
		handleRearrangeOptions("optionsOrder", index, newIndex);
	}

	function handleDeleteOption() {
		handleShowDeleteModal(() => handleRemoveOption({ optionToRemove: template, index }), template.label);
	}

	return (
		<div data-test="option-item-wrapper" className={styles.list_item_wrapper}>
			<div className={styles.option_item_wrapper}>
				<div className={styles.label_and_chevrons_wrapper}>
					<Button variant={"outlined"} colorPalette={color} onClick={toggleEditOpen} data-test="edit-me">
						{!editOpen ? "Edit Me" : "Close Edit"}
					</Button>
					<div
						style={{
							background: index !== 0 ? color.background.secondary : color.background.primary,
							border: `1px solid ${color.border.primary}`,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
						onClick={() => handleRearrange("UP")}>
						<SingleChevron color={color.utils.icon} direction="up" />
					</div>
					<div
						style={{
							background: !isLast ? color.background.secondary : color.background.primary,
							border: `1px solid ${color.border.primary}`,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
						onClick={() => handleRearrange()}>
						<SingleChevron color={color.utils.icon} direction="down" />
					</div>
					<div className={styles.option_label}>
						{!template.label || (template?.label && template.label == "") ? "Unlabeled Option" : template.label}
					</div>
				</div>
				<div className={styles.option_edit_btns}>
					<Button variant="destructive" colorPalette={color} onClick={handleDeleteOption}>
						Remove Option
					</Button>
				</div>
			</div>
			{editOpen ? (
				<div data-test="options-form-wrapper" className={styles.options_wrapper}>
					<UpdateTemplateForm
						data={index}
						template={template}
						submitCallback={handleSubmitOption}
						handleShowDeleteModal={handleShowDeleteModal}
						online={online}
						userDoc={userDoc}
						color={color}
						docType={"Option"}
						values={values}
						handleChange={handleChange}
						handleSubmit={handleSubmit}
						handleUpdateMultiplePropsAtOnce={handleUpdateMultiplePropsAtOnce}
					/>
				</div>
			) : null}
		</div>
	);
}

export default OptionsEditor;
